import React, { useState, useEffect } from 'react';
import { X, Eye, EyeOff } from 'lucide-react';
import { BrokerAccount, UpdateBrokerAccount } from '../../types';
import ErrorAlert from '../common/ErrorAlert';
import Button from '../ui/Button';
import FocusedDialog from '../ui/FocusedDialog';
import { isWhiteLabel } from '../../config/tenantConfig';
import {
  formatCheckStart,
  parseCheckStart,
  isCheckStartInRange,
  CHECK_START_RANGE_LABEL,
} from '../../utils/checkStart';

const FOCUS_RING = isWhiteLabel() ? 'focus:ring-zebu-500 focus:border-zebu-500' : 'focus:ring-blue-500 focus:border-blue-500';
const TOGGLE_FOCUS = isWhiteLabel() ? 'focus:ring-zebu-500' : 'focus:ring-blue-500';
const TOGGLE_ON = isWhiteLabel() ? 'bg-zebu-600' : 'bg-blue-600';

interface EditBrokerAccountFormProps {
  open: boolean;
  account: BrokerAccount | null;
  onClose: () => void;
  onSubmit: (clientId: string, updateData: UpdateBrokerAccount) => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
  onClearError?: () => void;
}

const EditBrokerAccountForm: React.FC<EditBrokerAccountFormProps> = ({
  open,
  account,
  onClose,
  onSubmit,
  isLoading = false,
  error,
  onClearError,
}) => {
  const [formData, setFormData] = useState<UpdateBrokerAccount>({
    api_key: '',
    api_secret: '',
    capital: 0,
    description: '',
  });
  const [capitalInputValue, setCapitalInputValue] = useState('0');
  const [checkStartInput, setCheckStartInput] = useState(formatCheckStart());
  const [checkStartError, setCheckStartError] = useState<string | undefined>(undefined);

  const [showApiSecret, setShowApiSecret] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showTotpKey, setShowTotpKey] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof UpdateBrokerAccount, string>>>({});

  const supportsAutoLogin = account?.broker_name === 'zerodha' || account?.broker_name === 'zebu';
  // Zebu uses the platform-level OAuth app — api_key/api_secret are
  // managed by the platform, not the user. Hide the fields for Zebu only.
  const isZebu = account?.broker_name === 'zebu';

  useEffect(() => {
    if (account) {
      const initialCapital = account.capital || 0;
      setFormData({
        api_key: '',
        api_secret: '',
        capital: initialCapital,
        description: account.description || '',
        auto_login_enabled: account.auto_login_enabled ?? false,
        password: '',
        totp_key: '',
      });
      setCapitalInputValue(String(initialCapital));
      setCheckStartInput(formatCheckStart(account.check_start_hour, account.check_start_minute));
      setCheckStartError(undefined);
    }
  }, [account]);

  const handleInputChange = (field: keyof UpdateBrokerAccount) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    let value: string | number = event.target.value;
    
    if (field === 'capital') {
      setCapitalInputValue(String(value));
      const parsedValue = Number(value);
      if (!Number.isNaN(parsedValue)) {
        value = parsedValue;
      } else {
        return;
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    if (fieldErrors[field]) {
      setFieldErrors(prev => ({
        ...prev,
        [field]: undefined,
      }));
    }

    if (error && onClearError) {
      onClearError();
    }
  };

  const handleCapitalBlur = () => {
    const trimmedValue = capitalInputValue.trim();
    const parsedValue = Number(trimmedValue);
    const finalValue = trimmedValue === '' || Number.isNaN(parsedValue) ? 0 : parsedValue;
    setFormData(prev => ({
      ...prev,
      capital: finalValue,
    }));
    setCapitalInputValue(String(finalValue));
  };

  const validateForm = (): boolean => {
    const errors: Partial<Record<keyof UpdateBrokerAccount, string>> = {};

    if (formData.capital !== undefined && formData.capital <= 0) {
      errors.capital = 'Capital must be greater than 0';
    }

    if (formData.auto_login_enabled) {
      // Only require password/totp if they weren't previously set (blank = keep existing)
      // We can't detect existing values (never returned by API), so warn only if toggling ON
      if (account && !account.auto_login_enabled) {
        if (!formData.password?.trim()) {
          errors.password = 'Password is required when enabling auto-login';
        }
        if (!formData.totp_key?.trim()) {
          errors.totp_key = 'TOTP Secret Key is required when enabling auto-login';
        }
      }
    }

    setFieldErrors(errors);

    // Check start time: validate only when the user actually changed it, so an
    // out-of-range legacy/stored value can't block editing unrelated fields
    // (capital/description). A changed value must parse and be within [09:16, 12:00].
    const currentCheckStart = formatCheckStart(account?.check_start_hour, account?.check_start_minute);
    const checkStartChanged = checkStartInput.trim() !== currentCheckStart;
    const parsed = parseCheckStart(checkStartInput);
    const checkStartOk =
      !checkStartChanged || (!!parsed && isCheckStartInRange(parsed.hour, parsed.minute));
    setCheckStartError(checkStartOk ? undefined : `Start time must be between ${CHECK_START_RANGE_LABEL}.`);

    return Object.keys(errors).length === 0 && checkStartOk;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!account || !validateForm()) {
      return;
    }

    const updateData: UpdateBrokerAccount = {};
    
    if (formData.api_key?.trim()) {
      updateData.api_key = formData.api_key.trim();
    }

    if (formData.api_secret?.trim()) {
      updateData.api_secret = formData.api_secret.trim();
    }

    if (formData.capital !== undefined && formData.capital > 0) {
      updateData.capital = formData.capital;
    }

    if (formData.description !== undefined) {
      updateData.description = formData.description.trim();
    }

    // Send check start only when it changed from the account's current effective
    // value (validateForm already guaranteed it parses and is in range).
    const currentCheckStart = formatCheckStart(account.check_start_hour, account.check_start_minute);
    if (checkStartInput.trim() !== currentCheckStart) {
      const parsed = parseCheckStart(checkStartInput);
      if (parsed) {
        updateData.check_start_hour = parsed.hour;
        updateData.check_start_minute = parsed.minute;
      }
    }

    if (formData.auto_login_enabled !== undefined) {
      updateData.auto_login_enabled = formData.auto_login_enabled;
    }

    if (formData.password?.trim()) {
      updateData.password = formData.password.trim();
    }

    if (formData.totp_key?.trim()) {
      updateData.totp_key = formData.totp_key.trim();
    }

    if (Object.keys(updateData).length === 0) {
      setFieldErrors({ api_key: 'At least one field must be updated' });
      return;
    }

    try {
      await onSubmit(account.client_id, updateData);
      handleClose();
    } catch (error) {
      // Error handling is managed by parent
    }
  };

  const handleClose = () => {
    setFormData({
      api_key: '',
      api_secret: '',
      capital: account?.capital || 0,
      description: account?.description || '',
    });
    setCapitalInputValue(String(account?.capital || 0));
    setCheckStartInput(formatCheckStart(account?.check_start_hour, account?.check_start_minute));
    setCheckStartError(undefined);
    setFieldErrors({});
    if (onClearError) {
      onClearError();
    }
    onClose();
  };

  if (!open || !account) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <FocusedDialog
        ariaLabel="Edit broker account"
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Edit Broker Account
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto">
          {error && (
            <div className="mb-4">
              <ErrorAlert message={error} onClose={onClearError} />
            </div>
          )}

          {/* Read-only account info */}
          <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Account Information</h3>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Broker:</span>
                <span className="text-gray-900 dark:text-white capitalize">{account.broker_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Client ID:</span>
                <span className="text-gray-900 dark:text-white">{account.client_id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Group:</span>
                <span className="text-gray-900 dark:text-white">{account.group}</span>
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              These fields cannot be modified after account creation.
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Capital *
              </label>
              <input
                type="number"
                min="1"
                step="0.01"
                value={capitalInputValue}
                onChange={handleInputChange('capital')}
                onBlur={handleCapitalBlur}
                className={`block w-full px-3 py-2 border rounded-lg focus:ring-2 ${FOCUS_RING} bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                  fieldErrors.capital
                    ? 'border-red-300 dark:border-red-600'
                    : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="100000"
              />
              {fieldErrors.capital && (
                <p className="mt-1 text-xs text-red-600">{fieldErrors.capital}</p>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={handleInputChange('description')}
                rows={3}
                className={`block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 ${FOCUS_RING} bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none`}
                placeholder="Optional description for this account"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Checks start time
              </label>
              <input
                type="time"
                aria-label="Checks start time"
                min={formatCheckStart(9, 16)}
                max={formatCheckStart(12, 0)}
                step={60}
                value={checkStartInput}
                onChange={(e) => { setCheckStartInput(e.target.value); setCheckStartError(undefined); }}
                className={`block w-40 px-3 py-2 border rounded-lg focus:ring-2 ${FOCUS_RING} bg-white dark:bg-gray-700 text-gray-900 dark:text-white tabular-nums ${checkStartError ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                When this account's per-minute checks begin (default 09:16). Positions
                entered before this time aren't managed until it.
              </p>
              {checkStartError && (
                <p className="mt-1 text-xs text-red-600">{checkStartError}</p>
              )}
            </div>

            {!isZebu && (
              <>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    API Key
                  </label>
                  <input
                    type="text"
                    value={formData.api_key}
                    onChange={handleInputChange('api_key')}
                    className={`block w-full px-3 py-2 border rounded-lg focus:ring-2 ${FOCUS_RING} bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                      fieldErrors.api_key
                        ? 'border-red-300 dark:border-red-600'
                        : 'border-gray-300 dark:border-gray-600'
                    }`}
                    placeholder="Leave empty to keep current API key"
                  />
                  {fieldErrors.api_key && (
                    <p className="mt-1 text-xs text-red-600">{fieldErrors.api_key}</p>
                  )}
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    API Secret
                  </label>
                  <div className="relative">
                    <input
                      type={showApiSecret ? 'text' : 'password'}
                      value={formData.api_secret}
                      onChange={handleInputChange('api_secret')}
                      className={`block w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 ${FOCUS_RING} bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                        fieldErrors.api_secret
                          ? 'border-red-300 dark:border-red-600'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder="Leave empty to keep current API secret"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowApiSecret(!showApiSecret)}
                    >
                      {showApiSecret ? (
                        <EyeOff className="h-5 w-5 text-gray-400" />
                      ) : (
                        <Eye className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                  {fieldErrors.api_secret && (
                    <p className="mt-1 text-xs text-red-600">{fieldErrors.api_secret}</p>
                  )}
                </div>
              </>
            )}

            {/* Auto-Login Section — Zerodha and Zebu only */}
            {supportsAutoLogin && (
              <div className="mb-4 p-4 border border-gray-200 dark:border-gray-600 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Enable Auto-Login</span>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Automatically refresh OAuth token every morning at 8:05 AM
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, auto_login_enabled: !prev.auto_login_enabled }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 ${TOGGLE_FOCUS} focus:ring-offset-2 ${
                      formData.auto_login_enabled ? TOGGLE_ON : 'bg-gray-200 dark:bg-gray-600'
                    }`}
                    aria-label="Toggle auto-login"
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formData.auto_login_enabled ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>

                {formData.auto_login_enabled && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Broker Login Password
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={formData.password || ''}
                          onChange={handleInputChange('password')}
                          className={`block w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 ${FOCUS_RING} bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                            fieldErrors.password ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                          }`}
                          placeholder="Leave empty to keep current password"
                        />
                        <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowPassword(!showPassword)}>
                          {showPassword ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                        </button>
                      </div>
                      {fieldErrors.password && <p className="mt-1 text-xs text-red-600">{fieldErrors.password}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        TOTP Secret Key
                      </label>
                      <div className="relative">
                        <input
                          type={showTotpKey ? 'text' : 'password'}
                          value={formData.totp_key || ''}
                          onChange={handleInputChange('totp_key')}
                          className={`block w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 ${FOCUS_RING} bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                            fieldErrors.totp_key ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                          }`}
                          placeholder="Leave empty to keep current TOTP key"
                        />
                        <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowTotpKey(!showTotpKey)}>
                          {showTotpKey ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                        </button>
                      </div>
                      {fieldErrors.totp_key && <p className="mt-1 text-xs text-red-600">{fieldErrors.totp_key}</p>}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <Button type="submit" disabled={isLoading} isLoading={isLoading}>
                Update Account
              </Button>
            </div>
          </form>
        </div>
      </FocusedDialog>
    </div>
  );
};

export default EditBrokerAccountForm;